import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  BarChart3,
  LineChart,
  TrendingUp,
  Database,
  FileSpreadsheet,
  Code2,
  Brain,
  Cpu,
  Binary,
  CheckCircle,
  Star,
  Zap,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Twitter,
  Linkedin,
  MessageCircle,
  Youtube,
  Globe,
  Image,
  FileJson,
  Table2,
} from 'lucide-react';
import { useThemeStore } from '../../../store/theme';

// CSS Keyframe animations
const keyframeStyles = `
  @keyframes float-data {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-12px) rotate(2deg); }
  }

  @keyframes pulse-violet {
    0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.4); }
    50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.7); }
  }

  @keyframes orbit-data {
    from { transform: rotate(0deg) translateX(110px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(110px) rotate(-360deg); }
  }

  @keyframes orbit-data-reverse {
    from { transform: rotate(360deg) translateX(95px) rotate(-360deg); }
    to { transform: rotate(0deg) translateX(95px) rotate(0deg); }
  }

  @keyframes data-stream {
    0% { background-position: 0% 0%; }
    100% { background-position: 0% 100%; }
  }

  @keyframes blink-cursor {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @keyframes chart-grow {
    0% { transform: scaleY(0); }
    100% { transform: scaleY(1); }
  }

  @keyframes number-float {
    0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
    50% { transform: translateY(-15px) scale(1.1); opacity: 0.8; }
  }

  @keyframes matrix-fall {
    0% { transform: translateY(-100%); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(100vh); opacity: 0; }
  }

  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .animate-float-data { animation: float-data 5s ease-in-out infinite; }
  .animate-pulse-violet { animation: pulse-violet 3s ease-in-out infinite; }
  .animate-orbit-data { animation: orbit-data 18s linear infinite; }
  .animate-orbit-data-reverse { animation: orbit-data-reverse 14s linear infinite; }
  .animate-data-stream { animation: data-stream 20s linear infinite; }
  .animate-blink-cursor { animation: blink-cursor 1s step-end infinite; }
  .animate-chart-grow { animation: chart-grow 1s ease-out forwards; transform-origin: bottom; }
  .animate-number-float { animation: number-float 3s ease-in-out infinite; }
  .animate-matrix-fall { animation: matrix-fall 8s linear infinite; }
  .animate-spin-slow { animation: spin-slow 20s linear infinite; }
`;

// Helper function to render markdown-style bold text
const renderMessageContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Chat demo messages
const chatMessages = [
  { role: 'user', message: "I have a CSV file with 50,000 sales records. Can you help me find patterns and anomalies?" },
  { role: 'assistant', message: "I'd be happy to analyze your sales data! I've processed your CSV file and found several interesting insights:\n\n📊 **Key Findings:**\n• Sales peak on Tuesdays and Fridays (23% above average)\n• Product category 'Electronics' shows a 47% YoY growth\n• 3 anomalies detected: unusual spikes on March 15, July 8, and October 22\n• Strong correlation (r=0.89) between marketing spend and sales\n\nWould you like me to generate visualizations or dive deeper into any specific pattern?" },
  { role: 'user', message: "Yes, please create a predictive model for next quarter's sales." },
  { role: 'assistant', message: "I've built a time-series forecasting model using your historical data. Here are the predictions for Q2:\n\n📈 **Q2 Forecast:**\n• April: $2.4M (+12% vs Q1)\n• May: $2.7M (+15% vs Q1)\n• June: $3.1M (+22% vs Q1)\n\n**Model Accuracy:** 94.2% (validated against last 6 months)\n**Confidence Interval:** ±8%\n\nI've also identified 3 key drivers you can optimize to exceed these projections. Want me to explain the algorithm or export the detailed report?" },
];

// Testimonials
const testimonials = [
  {
    name: 'Dr. Sarah Chen',
    role: 'Head of Data Science',
    company: 'QuantumMetrics',
    avatar: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=100&h=100&fit=crop&crop=face',
    quote: 'Atlas transformed our data analysis workflow. What used to take our team days now happens in minutes. The algorithm explanations are textbook-quality.',
    rating: 5,
  },
  {
    name: 'Michael Rodriguez',
    role: 'VP of Analytics',
    company: 'DataDriven Inc',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    quote: 'The ability to upload images of algorithm problems and get step-by-step solutions is game-changing. Atlas is like having a PhD data scientist on call 24/7.',
    rating: 5,
  },
  {
    name: 'Emily Watson',
    role: 'Senior Data Engineer',
    company: 'CloudScale',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    quote: 'From CSV analysis to complex statistical modeling, Atlas handles it all. Our decision-making is now 100% data-driven thanks to the insights it provides.',
    rating: 5,
  },
];

// Capabilities with unique images
const capabilities = [
  {
    icon: Code2,
    title: 'Algorithm Design & Writing',
    description: 'Get textbook-quality algorithms with Big O analysis, pseudocode, and optimized implementations in any programming language.',
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&h=400&fit=crop',
  },
  {
    icon: FileSpreadsheet,
    title: 'Data File Processing',
    description: 'Upload CSV, JSON, Excel, or any data file. Atlas analyzes, cleans, transforms, and extracts actionable insights automatically.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
  },
  {
    icon: Image,
    title: 'Image-Based Problem Solving',
    description: 'Take a photo of algorithm questions, flowcharts, or data diagrams. Atlas reads and solves them with detailed explanations.',
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&h=400&fit=crop',
  },
  {
    icon: LineChart,
    title: 'Statistical Analysis',
    description: 'From descriptive statistics to advanced regression, hypothesis testing, and time-series forecasting with confidence intervals.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
  },
  {
    icon: Brain,
    title: 'Pattern Recognition',
    description: 'Identify trends, anomalies, correlations, and hidden patterns in your data that human analysis might miss.',
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=600&h=400&fit=crop',
  },
  {
    icon: TrendingUp,
    title: 'Predictive Modeling',
    description: 'Build forecasting models, make data-driven predictions, and understand the key drivers behind your metrics.',
    image: 'https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=600&h=400&fit=crop',
  },
];

// Discover more tasks with unique GIFs
const discoverTasks = [
  {
    title: 'Analyze Data Files',
    description: 'Upload CSV, JSON, or Excel for instant insights',
    gif: 'https://media.giphy.com/media/JWuBH9rCO2uZuHBFpm/giphy.gif',
    prompt: 'Help me analyze this CSV file and find key patterns',
  },
  {
    title: 'Write Algorithms',
    description: 'Get optimized code with complexity analysis',
    gif: 'https://media.giphy.com/media/26tn33aiTi1jkl6H6/giphy.gif',
    prompt: 'Write an efficient sorting algorithm with O(n log n) complexity',
  },
  {
    title: 'Solve Math Problems',
    description: 'From statistics to calculus and beyond',
    gif: 'https://media.giphy.com/media/3o7btPCcdNniyf0ArS/giphy.gif',
    prompt: 'Help me solve this statistical hypothesis testing problem',
  },
  {
    title: 'Create Visualizations',
    description: 'Generate charts and data visualizations',
    gif: 'https://media.giphy.com/media/l46Cy1rHbQ92uuLXa/giphy.gif',
    prompt: 'Create a visualization strategy for my sales data',
  },
  {
    title: 'Build Predictive Models',
    description: 'Forecast trends and future outcomes',
    gif: 'https://media.giphy.com/media/xT9C25UNTwfZuk85WP/giphy.gif',
    prompt: 'Build a predictive model for customer churn',
  },
  {
    title: 'Explain Algorithms',
    description: 'Step-by-step algorithm breakdowns',
    gif: 'https://media.giphy.com/media/L1R1tvI9svkIWwpVYr/giphy.gif',
    prompt: 'Explain how the Dijkstra algorithm works with examples',
  },
];

// Related employees
const relatedEmployees = [
  { id: 'agent', name: 'Agent', role: 'Conversational Agent', avatar: '🤖', gradient: 'from-sky-500 to-blue-500' },
  { id: 'sage', name: 'Sage', role: 'Business Strategist', avatar: '🧠', gradient: 'from-emerald-500 to-green-500' },
  { id: 'code', name: 'Code', role: 'Developer Assistant', avatar: '💻', gradient: 'from-slate-500 to-gray-500' },
];

// Stats
const stats = [
  { label: 'Algorithms Written', value: '5M+', icon: Code2 },
  { label: 'Data Files Analyzed', value: '12M+', icon: FileSpreadsheet },
  { label: 'Accuracy Rate', value: '99.2%', icon: CheckCircle },
  { label: 'Languages Supported', value: '25+', icon: Globe },
];

// Supported file types
const fileTypes = [
  { name: 'CSV', icon: Table2, color: 'from-green-500 to-emerald-500' },
  { name: 'JSON', icon: FileJson, color: 'from-yellow-500 to-orange-500' },
  { name: 'Excel', icon: FileSpreadsheet, color: 'from-green-600 to-green-500' },
  { name: 'Images', icon: Image, color: 'from-purple-500 to-pink-500' },
  { name: 'SQL', icon: Database, color: 'from-blue-500 to-cyan-500' },
];

export default function AtlasEmployee() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(chatRef, { once: true, margin: '-100px' });

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate chat messages when in view
  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const timeout = setTimeout(() => {
        setVisibleMessages((prev) => prev + 1);
      }, 1200);
      return () => clearTimeout(timeout);
    }
  }, [isInView, visibleMessages]);

  // JSON-LD Schema
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Atlas - AI Data Analyst & Algorithm Expert',
    applicationCategory: 'BusinessApplication',
    description: 'AI-powered data analyst expert in algorithms, data analysis, and computational problem-solving. Writes textbook-quality algorithms and makes data-driven decisions.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '4521',
    },
    operatingSystem: 'Web-based',
    featureList: [
      'Algorithm design and writing',
      'Data file processing (CSV, JSON, Excel)',
      'Image-based algorithm problem solving',
      'Statistical analysis and modeling',
      'Pattern recognition and anomaly detection',
      'Predictive modeling and forecasting',
      'Big O complexity analysis',
      'Multi-language code generation',
    ],
  };

  return (
    <>
      <Helmet>
        <title>Atlas - AI Data Analyst & Algorithm Expert | SweetPilot</title>
        <meta
          name="description"
          content="Meet Atlas, your AI data analyst and algorithm expert. Analyze data files, write optimized algorithms, solve complex problems, and make data-driven decisions."
        />
        <meta
          name="keywords"
          content="AI data analyst, algorithm expert, data analysis AI, CSV analysis, statistical modeling, predictive analytics, machine learning, data science AI"
        />
        <link rel="canonical" href="https://sweetpilot.com/employees/atlas" />

        {/* Open Graph */}
        <meta property="og:title" content="Atlas - AI Data Analyst & Algorithm Expert | SweetPilot" />
        <meta
          property="og:description"
          content="Expert in algorithms, data analysis, and computational problem-solving. Upload data files, get insights, and write optimized code."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sweetpilot.com/employees/atlas" />
        <meta property="og:image" content="https://sweetpilot.com/og-atlas.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Atlas - AI Data Analyst & Algorithm Expert" />
        <meta
          name="twitter:description"
          content="Expert in algorithms, data analysis, and computational problem-solving. Upload data files, get insights, and write optimized code."
        />
        <meta name="twitter:image" content="https://sweetpilot.com/og-atlas.png" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{keyframeStyles}</style>

      <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        {/* Floating Data Elements Background */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-number-float"
              style={{
                left: `${8 + i * 10}%`,
                top: `${15 + (i % 4) * 20}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + i * 0.3}s`,
              }}
            >
              <span className={`text-2xl font-mono font-bold ${isDark ? 'text-violet-500/20' : 'text-violet-400/25'}`}>
                {['01', '10', '∑', 'π', '∞', 'Δ', 'λ', '{}', '[]', '//'][i]}
              </span>
            </div>
          ))}
        </div>

        {/* Navigation Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-dark-900/80' : 'bg-white/80'} backdrop-blur-xl border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/employees" className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Employees</span>
              </Link>
              <Link
                to="/dashboard/employees/atlas"
                className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-violet-500/25 transition-all"
              >
                Chat with Atlas
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-purple-500/10" />

          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(${isDark ? '#fff' : '#000'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? '#fff' : '#000'} 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/employees"
                    className={`transition-colors ${isDark ? 'hover:text-violet-400' : 'hover:text-violet-600'}`}
                  >
                    AI Employees
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  Atlas
                </li>
              </ol>
            </nav>

            <div className="text-center">
              {/* Avatar with orbiting icons */}
              <div className="relative w-48 h-48 mx-auto mb-8">
                {/* Orbiting icons */}
                <div className="absolute inset-0 animate-orbit-data">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                      <BarChart3 className="w-6 h-6 text-violet-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-data-reverse">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                      <Binary className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-data" style={{ animationDelay: '-9s' }}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                      <Cpu className="w-6 h-6 text-indigo-500" />
                    </div>
                  </div>
                </div>

                {/* Main avatar */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-36 h-36 rounded-3xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center animate-float-data animate-pulse-violet">
                    <span className="text-7xl">📊</span>
                  </div>
                </motion.div>
              </div>

              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className={`text-5xl md:text-7xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Meet{' '}
                  <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                    Atlas
                  </span>
                </h1>
                <p className={`text-xl md:text-2xl mb-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                  Your AI Data Analyst & Algorithm Expert
                </p>
                <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  Expert in algorithms, data analysis, and computational problem-solving. Writes textbook-quality algorithms, analyzes data files, and makes data-driven decisions.
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-8"
              >
                <Link
                  to="/dashboard/employees/atlas"
                  className="group px-8 py-4 bg-gradient-to-r from-violet-500 to-purple-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-violet-500/30 transition-all flex items-center gap-2"
                >
                  Start Analyzing Data
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#capabilities"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-violet-500/50 text-violet-400 hover:bg-violet-500/10'
                      : 'border-violet-500 text-violet-600 hover:bg-violet-50'
                  }`}
                >
                  Explore Features
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
              >
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}
                  >
                    <stat.icon className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-violet-400' : 'text-violet-500'}`} />
                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chat Demo Section */}
        <section ref={chatRef} className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                See Atlas in Action
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Watch how Atlas transforms raw data into actionable insights
              </p>
            </motion.div>

            {/* Chat Window */}
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-900 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              {/* Chat Header */}
              <div className={`px-6 py-4 border-b ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                    <span className="text-lg">📊</span>
                  </div>
                  <div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Atlas</div>
                    <div className="flex items-center gap-1 text-sm text-emerald-500">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      Analyzing data...
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="p-6 space-y-4 min-h-[450px]">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] p-4 rounded-2xl whitespace-pre-line ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white'
                            : isDark
                            ? 'bg-dark-700 text-gray-200'
                            : 'bg-white text-slate-700 shadow-sm'
                        }`}
                      >
                        <span className="whitespace-pre-line">{renderMessageContent(msg.message)}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing indicator */}
                {isInView && visibleMessages < chatMessages.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-dark-700' : 'bg-white shadow-sm'}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Supported File Types */}
        <section className={`py-16 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className={`text-2xl md:text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Works With Your Data
              </h2>
              <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Upload any file format and Atlas will analyze it instantly
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-4">
              {fileTypes.map((type, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl ${
                    isDark ? 'bg-dark-800/50' : 'bg-white shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${type.color}`}>
                    <type.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{type.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Powerful{' '}
                <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                  Data Capabilities
                </span>
              </h2>
              <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                From algorithm design to predictive modeling, Atlas handles complex data challenges with ease
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group rounded-2xl overflow-hidden border ${
                    isDark ? 'bg-dark-800/50 border-white/5 hover:border-violet-500/30' : 'bg-white border-slate-200 hover:border-violet-300'
                  } transition-all hover:shadow-xl hover:shadow-violet-500/10`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="p-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500">
                        <capability.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {capability.title}
                    </h3>
                    <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                      {capability.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Discover More Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Discover What Atlas Can Do
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Click any task to try it with Atlas
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discoverTasks.map((task, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/dashboard/employees/atlas?prompt=${encodeURIComponent(task.prompt)}`}
                    className={`group relative block h-64 rounded-2xl overflow-hidden border ${
                      isDark ? 'border-white/10 hover:border-violet-500/50' : 'border-slate-200 hover:border-violet-300'
                    } transition-all`}
                  >
                    {/* Background Image/GIF */}
                    <div className="absolute inset-0">
                      <img
                        src={task.gif}
                        alt={task.title}
                        className="w-full h-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                      />
                      <div className={`absolute inset-0 ${isDark ? 'bg-dark-800' : 'bg-white'} group-hover:opacity-80 transition-opacity`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <div className="mb-auto">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <BarChart3 className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {task.title}
                      </h3>
                      <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {task.description}
                      </p>
                      <div className={`mt-4 flex items-center gap-2 text-violet-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <span>Try this now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Trusted by Data Teams
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                See how Atlas is transforming data analysis
              </p>
            </motion.div>

            {/* Testimonial Carousel */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className={`p-8 rounded-2xl ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4 justify-center">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className={`text-xl text-center mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    "{testimonials[currentTestimonial].quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="text-left">
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTestimonial
                        ? 'w-8 bg-gradient-to-r from-violet-500 to-purple-500'
                        : isDark
                        ? 'bg-white/20 hover:bg-white/40'
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Employees */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Meet More AI Employees
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Build your complete AI team
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedEmployees.map((employee, index) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/employees/${employee.id}`}
                    className={`group block p-6 rounded-2xl border transition-all ${
                      isDark
                        ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                        : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{employee.avatar}</span>
                    </div>
                    <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name}
                    </h3>
                    <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {employee.role}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-violet-500/10 via-dark-900 to-purple-500/10' : 'bg-gradient-to-br from-violet-50 via-white to-purple-50'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center animate-float-data">
                <span className="text-5xl">📊</span>
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Ready to Unlock Your Data's Potential?
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Start analyzing data and writing algorithms with Atlas today
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/employees/atlas"
                  className="group px-8 py-4 bg-gradient-to-r from-violet-500 to-purple-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-violet-500/30 transition-all flex items-center gap-2"
                >
                  Chat with Atlas Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/employees"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-white/20 text-white hover:bg-white/5'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  View All Employees
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
              {/* Logo */}
              <div className="col-span-2">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                    alt="SweetPilot"
                    className="w-48 object-contain"
                  />
                </Link>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                  AI employees that never sleep. Build, grow, and scale your
                  business 24/7.
                </p>
                <div className="flex space-x-3">
                  {[
                    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/flowai' },
                    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/flowai' },
                    { name: 'Discord', icon: MessageCircle, href: 'https://discord.gg/flowai' },
                    { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@flowai' },
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        isDark
                          ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                          : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                      }`}
                    >
                      <span className="sr-only">{social.name}</span>
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Links */}
              {[
                {
                  title: 'Product',
                  links: [
                    { name: 'AI Employees', href: '/employees' },
                    { name: 'Features', href: '/features' },
                    { name: 'Pricing', href: '/pricing' },
                    { name: 'Integrations', href: '/integrations' },
                    { name: 'API', href: '/api' },
                  ],
                },
                {
                  title: 'Company',
                  links: [
                    { name: 'About', href: '/about' },
                    { name: 'Blog', href: '/blog' },
                    { name: 'Careers', href: '/careers' },
                    { name: 'Press', href: '/press' },
                    { name: 'Contact', href: '/contact' },
                  ],
                },
                {
                  title: 'Resources',
                  links: [
                    { name: 'Documentation', href: '/docs' },
                    { name: 'Help Center', href: '/help' },
                    { name: 'Community', href: '/community' },
                    { name: 'Templates', href: '/templates' },
                    { name: 'Roadmap', href: '/roadmap' },
                    { name: 'Status', href: '/status' },
                  ],
                },
                {
                  title: 'Legal',
                  links: [
                    { name: 'Privacy', href: '/privacy' },
                    { name: 'Terms', href: '/terms' },
                    { name: 'Security', href: '/security' },
                    { name: 'Cookies', href: '/cookies' },
                    { name: 'GDPR', href: '/gdpr' },
                    { name: 'Refund Policy', href: '/refund-policy' },
                  ],
                },
              ].map((column) => (
                <div key={column.title}>
                  <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                  <ul className="space-y-3">
                    {column.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
              <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                &copy; {new Date().getFullYear()} SweetPilot. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Terms of Service
                </Link>
                <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
